import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import type { TagChipVariant } from "@/types/tag-chip-variant";
import { getDesignTokens } from "@/theme/designTokens";
import { fontWeight } from "@/theme/typography";

export type { TagChipVariant } from "@/types/tag-chip-variant";

type Props = {
  label: string;
  icon?: ReactNode;
  color?: string;
  variant?: TagChipVariant;
  /** Compact sizing for dense menu rows. */
  size?: "default" | "compact";
  /** When false, suppresses hover lift (presentational badges). */
  interactive?: boolean;
  /** Promo-only muted paint. Does not change role or click handling. */
  disabled?: boolean;
  /** Strip chip chrome — parent supplies surface styling. */
  unstyled?: boolean;
};

/**
 * Shared chip. `promo` = Rich Premium Gold discount / offer badge.
 * Icon is a subtle accent — label remains the primary signal.
 */
function TagChip({
  label,
  icon,
  color,
  variant = "subtle",
  size = "default",
  interactive = true,
  disabled = false,
  unstyled = false,
}: Props) {
  const theme = useTheme();
  const tokens = getDesignTokens(theme);
  void color;

  const isPromo = variant === "promo";
  const isCompact = size === "compact";
  const promo = tokens.tag.promo;

  const getStyles = () => {
    if (isPromo) {
      return {
        backgroundColor: disabled ? promo.disabledBg : promo.bg,
        color: disabled ? promo.disabledColor : promo.color,
        border: `${promo.borderWidthPx}px solid ${
          disabled ? promo.disabledBorder : promo.border
        }`,
        boxShadow: disabled ? promo.pressedShadow : promo.shadow,
        minHeight: promo.minHeightPx,
        justifyContent: "center" as const,
        opacity: disabled ? 1 : undefined,
      };
    }
    if (variant === "filled") {
      return {
        background: tokens.selection.surfaceTinted,
        color: theme.palette.text.primary,
        border: `1px solid ${tokens.selection.borderSoft}`,
      };
    }
    if (variant === "outline") {
      return {
        background: "transparent",
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
      };
    }
    return {
      background: theme.palette.action.hover,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
    };
  };

  const styles = getStyles();
  const chipStyles = unstyled
    ? {
        background: "transparent",
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
        border: "none",
        boxShadow: "none",
        minHeight: 0,
      }
    : styles;

  const labelWeight = isPromo ? fontWeight.semibold : fontWeight.medium;
  const promoPadL = isCompact ? promo.paddingLeftPx.compact : promo.paddingLeftPx.default;
  const promoPadR = isCompact ? promo.paddingRightPx.compact : promo.paddingRightPx.default;
  const promoPadY = isCompact ? promo.paddingYPx.compact : promo.paddingYPx.default;
  const promoFontPx = isCompact ? promo.fontSizePx.compact : promo.fontSizePx.default;
  const chipGap = isPromo
    ? `${promo.iconGapPx}px`
    : isCompact
      ? "3px"
      : "4px";

  return (
    <Box
      data-testid={isPromo ? "discount-offer-chip" : undefined}
      aria-disabled={isPromo && disabled ? true : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: chipGap,
        px: isPromo ? undefined : isCompact ? "6px" : "8px",
        pl: isPromo ? `${promoPadL}px` : undefined,
        pr: isPromo ? `${promoPadR}px` : undefined,
        py: isPromo ? `${promoPadY}px` : isCompact ? "3px" : "4px",
        borderRadius: "9999px",
        fontSize: isPromo
          ? theme.typography.pxToRem(promoFontPx)
          : isCompact
            ? "11px"
            : "11.5px",
        fontWeight: labelWeight,
        lineHeight: 1,
        whiteSpace: isPromo ? "normal" : "nowrap",
        textAlign: isPromo ? "center" : undefined,
        boxSizing: "border-box",
        ...chipStyles,
        ...(isPromo && interactive && !disabled
          ? {
              cursor: "pointer",
              transition:
                "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                backgroundColor: promo.hoverBg,
                borderColor: promo.hoverBorder,
                boxShadow: promo.hoverShadow,
              },
              "&:active": {
                backgroundColor: promo.pressedBg,
                borderColor: promo.pressedBorder,
                boxShadow: promo.pressedShadow,
              },
            }
          : {}),
        ...(interactive && !isPromo
          ? {
              transition:
                "transform 140ms ease, box-shadow 180ms ease, background-color 180ms ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[1],
                background: tokens.selection.surfaceTinted,
              },
            }
          : {}),
      }}
    >
      {icon ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            lineHeight: 0,
            color: isPromo
              ? disabled
                ? promo.disabledIcon
                : promo.icon
              : "inherit",
            // Optical vertical balance with uppercase label
            transform: isPromo
              ? `translateY(${promo.iconOpticalOffsetYPx}px)`
              : undefined,
            "& > svg": {
              display: "block",
              width: isPromo ? promo.iconSizePx : isCompact ? 10 : 11,
              height: isPromo ? promo.iconSizePx : isCompact ? 10 : 11,
              flexShrink: 0,
            },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography
        component="span"
        sx={{
          fontSize: isPromo
            ? theme.typography.pxToRem(promoFontPx)
            : isCompact
              ? "11px"
              : "12px",
          fontWeight: labelWeight,
          lineHeight: isPromo ? 1.2 : 1,
          letterSpacing: isPromo ? promo.letterSpacing : undefined,
          textTransform: isPromo ? "uppercase" : undefined,
          color: "inherit",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default TagChip;
